import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { letzteSaisonId, merkeSaisonId, saisonIdZuName } from '../utils/saison';
import './AppLayout.css';

// Gemeinsamer Rahmen für alle eingeloggten Seiten.
// Desktop: Kopfzeile mit Saison-Reitern (Tippen / Ranking / Bonus) und den
// übrigen Zielen rechts. Handy: schlanke Kopfzeile, feste Leiste unten mit
// vier Zielen, "Mehr" öffnet die restlichen Seiten als Liste.
// variante="admin" bekommt eine eigene, deutlich abgesetzte Kopfzeile.

const SAISON_MUSTER = ['/tippspiel/:saisonId/*', '/saison/:saisonId/*'];

function saisonAusPfad(pathname) {
  for (const muster of SAISON_MUSTER) {
    const treffer = matchPath(muster, pathname);
    if (treffer?.params?.saisonId) return treffer.params.saisonId;
  }
  return null;
}

function istTippenAktiv(pathname) {
  return Boolean(matchPath('/tippspiel/:saisonId', pathname) || matchPath('/saison/:saisonId', pathname));
}
function istRankingAktiv(pathname) {
  return Boolean(matchPath('/saison/:saisonId/ranking', pathname));
}
function istBonusAktiv(pathname) {
  return Boolean(
    matchPath('/tippspiel/:saisonId/bonusfragen', pathname) ||
    matchPath('/saison/:saisonId/bonusfragen', pathname)
  );
}

function AppLayout({ variante = 'spieler' }) {
  const { userProfile, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mehrOffen, setMehrOffen] = useState(false);

  const saisonImPfad = saisonAusPfad(location.pathname);
  useEffect(() => {
    if (saisonImPfad) merkeSaisonId(saisonImPfad);
  }, [saisonImPfad]);
  const saisonId = saisonImPfad || letzteSaisonId();

  // "Mehr"-Liste bei jedem Seitenwechsel schließen
  useEffect(() => {
    setMehrOffen(false);
  }, [location.pathname]);

  const istAdmin = userProfile?.isAdmin === true;
  const pfad = location.pathname;

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout-Fehler:', error);
    }
  };

  const saisonZiele = [
    { zu: `/tippspiel/${saisonId}`, label: 'Tippen', icon: '🏈', aktiv: istTippenAktiv(pfad) },
    { zu: `/saison/${saisonId}/ranking`, label: 'Ranking', icon: '🏆', aktiv: istRankingAktiv(pfad) },
    { zu: `/tippspiel/${saisonId}/bonusfragen`, label: 'Bonus', icon: '🎯', aktiv: istBonusAktiv(pfad) }
  ];

  const weitereZiele = [
    { zu: '/saison-auswahl', label: 'Saisons', icon: '📅' },
    { zu: '/hall-of-fame', label: 'Hall of Fame', icon: '🏛️' },
    { zu: '/board', label: 'Board', icon: '💬' },
    { zu: '/faq', label: 'FAQ', icon: '❓' },
    { zu: '/einstellungen', label: 'Einstellungen', icon: '⚙️' }
  ];

  const mehrAktiv = !saisonZiele.some(z => z.aktiv) && variante !== 'admin';

  if (variante === 'admin') {
    return (
      <div className="app-shell app-shell--admin">
        <header className="app-header app-header--admin">
          <div className="app-header__inner">
            <Link to="/saison-auswahl" className="app-brand">
              <span className="app-brand__icon">🛠️</span>
              <span className="app-brand__text">Admin-Bereich</span>
            </Link>
            <span className="app-header__hinweis">Angemeldet als {userProfile?.displayName || 'Admin'}</span>
            <Link to={`/tippspiel/${saisonId}`} className="app-button app-button--hell">
              ← Zum Tippspiel
            </Link>
          </div>
        </header>
        <main className="app-main app-main--admin">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="app-header__inner">
          <Link to="/saison-auswahl" className="app-brand" title="Saison wechseln">
            <span className="app-brand__icon">🏈</span>
            <span className="app-brand__text">Kessel MVP</span>
            <span className="app-brand__saison">{saisonIdZuName(saisonId)}</span>
          </Link>

          <nav className="app-nav app-nav--saison" aria-label="Saison">
            {saisonZiele.map(ziel => (
              <Link
                key={ziel.zu}
                to={ziel.zu}
                className={`app-nav__link${ziel.aktiv ? ' ist-aktiv' : ''}`}
              >
                <span className="app-nav__icon">{ziel.icon}</span>
                {ziel.label}
              </Link>
            ))}
          </nav>

          <nav className="app-nav app-nav--weitere" aria-label="Weitere Seiten">
            {weitereZiele.map(ziel => (
              <NavLink
                key={ziel.zu}
                to={ziel.zu}
                className={({ isActive }) => `app-nav__link app-nav__link--klein${isActive ? ' ist-aktiv' : ''}`}
              >
                {ziel.label}
              </NavLink>
            ))}
            {istAdmin && (
              <NavLink to="/admin" className="app-nav__link app-nav__link--klein app-nav__link--admin">
                Admin
              </NavLink>
            )}
            <button type="button" onClick={handleLogout} className="app-nav__link app-nav__link--klein app-nav__logout">
              Abmelden
            </button>
          </nav>
        </div>
      </header>

      <main className="app-main">
        <Outlet />
      </main>

      {/* Handy: feste Leiste unten */}
      <nav className="app-tabbar" aria-label="Hauptnavigation">
        {saisonZiele.map(ziel => (
          <Link
            key={ziel.zu}
            to={ziel.zu}
            className={`app-tabbar__tab${ziel.aktiv && !mehrOffen ? ' ist-aktiv' : ''}`}
          >
            <span className="app-tabbar__icon">{ziel.icon}</span>
            <span className="app-tabbar__label">{ziel.label}</span>
          </Link>
        ))}
        <button
          type="button"
          className={`app-tabbar__tab${mehrOffen || mehrAktiv ? ' ist-aktiv' : ''}`}
          onClick={() => setMehrOffen(o => !o)}
          aria-expanded={mehrOffen}
        >
          <span className="app-tabbar__icon">☰</span>
          <span className="app-tabbar__label">Mehr</span>
        </button>
      </nav>

      {mehrOffen && (
        <div className="app-mehr" onClick={() => setMehrOffen(false)}>
          <div className="app-mehr__liste" onClick={e => e.stopPropagation()}>
            {weitereZiele.map(ziel => (
              <NavLink
                key={ziel.zu}
                to={ziel.zu}
                className={({ isActive }) => `app-mehr__eintrag${isActive ? ' ist-aktiv' : ''}`}
              >
                <span className="app-mehr__icon">{ziel.icon}</span>
                {ziel.label}
              </NavLink>
            ))}
            {istAdmin && (
              <NavLink to="/admin" className="app-mehr__eintrag app-mehr__eintrag--admin">
                <span className="app-mehr__icon">🛠️</span>
                Admin-Bereich
              </NavLink>
            )}
            <button type="button" onClick={handleLogout} className="app-mehr__eintrag app-mehr__eintrag--logout">
              <span className="app-mehr__icon">🚪</span>
              Abmelden
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppLayout;
